import { useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { get } from 'aws-amplify/api';
import { useAuth } from '../contexts/AuthContext';
import './Sidebar.css';

/**
 * Sidebar Component - Vanilla JS Implementation
 *
 * OWASP Top 10 Compliant:
 * - All user inputs escaped via escapeHtml()
 * - Uses textContent instead of innerHTML where possible
 * - No eval() or Function() constructors
 * - CSP-friendly (no inline event handlers in HTML strings)
 */

// ========================================
// SECURITY: XSS Prevention Utilities
// ========================================

/**
 * Escape HTML special characters to prevent XSS attacks
 * OWASP recommendation: escape &, <, >, ", ', /, `, =
 */
function escapeHtml(str) {
  if (str === null || str === undefined) return '';
  const string = String(str);
  const htmlEscapes = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;',
    '`': '&#x60;',
    '=': '&#x3D;'
  };
  return string.replace(/[&<>"'`=/]/g, char => htmlEscapes[char]);
}

/**
 * Safely set text content without HTML interpretation
 */
function safeSetText(element, text) {
  if (element) {
    element.textContent = text !== null && text !== undefined ? String(text) : '';
  }
}

/**
 * Create an element with safe attributes
 */
function createElement(tag, attrs, textContent) {
  const el = document.createElement(tag);
  if (attrs) {
    Object.keys(attrs).forEach(key => {
      if (key === 'className') {
        el.className = attrs[key];
      } else if (key === 'dataset') {
        Object.keys(attrs[key]).forEach(dataKey => {
          el.dataset[dataKey] = escapeHtml(attrs[key][dataKey]);
        });
      } else if (key.startsWith('on')) {
        // Security: Don't allow inline event handlers from data
        console.warn('Inline event handlers not allowed for security');
      } else {
        el.setAttribute(key, escapeHtml(String(attrs[key])));
      }
    });
  }
  if (textContent !== undefined) {
    el.textContent = textContent;
  }
  return el;
}

// ========================================
// SECTION CONFIGURATION
// ========================================

const SECTIONS = [
  {
    id: 'app-features',
    title: 'App Features',
    icon: '⊞',
    defaultCollapsed: false,
    items: [
      { path: '/app', icon: '📱', label: 'App Management' },
      { path: '/events', icon: '📅', label: 'Event Calendar', badgeKey: 'pendingEvents' },
       { path: '/job-applications', icon: '📝', label: 'Job Applications', badgeKey: 'pendingApplications' },
    ],
  },
  {
    id: 'reservations',
    title: 'Reservations',
    icon: '📖',
    defaultCollapsed: false,
    items: [
      { path: '/resy', icon: '🍽️', label: 'Resy' },
    ],
  },
  {
    id: 'toast-features',
    title: 'Analytics',
    icon: '🍞',
    defaultCollapsed: false,
    items: [
      { path: '/labor', icon: '👥', label: 'Employees', badgeKey: 'pendingAvailability' },
      { path: '/analytics', icon: '📊', label: 'Analytics' },
      { path: '/kitchen', icon: '🍳', label: 'Kitchen' },
      { path: '/simulator', icon: '🧪', label: 'Toast Simulator', tag: 'DEV' },
    ],
  },
  {
    id: 'settings',
    title: 'Settings',
    icon: '⚙️',
    defaultCollapsed: false,
    items: [
      { path: '/settings', icon: '⚙️', label: 'Settings' },
    ],
  },
];

// ========================================
// SIDEBAR COMPONENT
// ========================================

const Sidebar = ({ mobileMenuOpen }) => {
  const containerRef = useRef(null);
  const sidebarRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { getAuthToken } = useAuth();

  // State refs (to avoid re-renders for vanilla JS)
  const stateRef = useRef({
    isCollapsed: localStorage.getItem('sidebarCollapsed') === 'true',
    sectionStates: loadSectionStates(),
    badgeCounts: {
      pendingEvents: 0,
      pendingAvailability: 0,
      pendingApplications: 0,
    },
  });

  function loadSectionStates() {
    try {
      const stored = localStorage.getItem('sidebarSections');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      // ignore
    }
    const defaults = {};
    SECTIONS.forEach(section => {
      defaults[section.id] = section.defaultCollapsed;
    });
    return defaults;
  }

  // Navigation handler
  const handleNavigation = useCallback((path) => {
    navigate(path);
  }, [navigate]);

  // Build sidebar DOM
  const buildSidebar = useCallback(() => {
    const state = stateRef.current;
    const currentPath = location.pathname;

    // Create sidebar element
    const sidebar = createElement('aside', {
      className: `sidebar ${state.isCollapsed ? 'collapsed' : ''} ${mobileMenuOpen ? 'mobile-open' : ''}`
    });

    // Header
    const header = createElement('div', { className: 'sidebar-header' });

    const brand = createElement('div', { className: 'sidebar-brand' });
    const logo = createElement('img', {
      className: 'sidebar-logo',
      src: '/chulas.png',
      alt: "Chula's Logo"
    });
    brand.appendChild(logo);

    if (!state.isCollapsed) {
      const title = createElement('h2', {}, "Chula's Admin");
      brand.appendChild(title);
    }
    header.appendChild(brand);

    const toggleBtn = createElement('button', {
      className: 'toggle-button',
      title: state.isCollapsed ? 'Expand' : 'Collapse'
    }, state.isCollapsed ? '→' : '←');

    toggleBtn.addEventListener('click', () => {
      state.isCollapsed = !state.isCollapsed;
      localStorage.setItem('sidebarCollapsed', state.isCollapsed);
      rebuildSidebar();
    });
    header.appendChild(toggleBtn);
    sidebar.appendChild(header);

    // Navigation
    const nav = createElement('nav', { className: 'sidebar-nav' });

    SECTIONS.forEach((section, index) => {
      // Separator
      if (index > 0) {
        nav.appendChild(createElement('div', { className: 'sidebar-separator' }));
      }

      const isCollapsed = state.sectionStates[section.id];
      const sectionEl = createElement('div', {
        className: `sidebar-section ${isCollapsed ? 'collapsed' : ''} ${section.className || ''}`,
        dataset: { sectionId: section.id }
      });

      // Section header
      const sectionHeader = createElement('div', { className: 'sidebar-section-header' });
      sectionHeader.setAttribute('role', 'button');
      sectionHeader.setAttribute('tabindex', '0');

      const sectionIcon = createElement('span', { className: 'sidebar-section-icon' }, section.icon);
      sectionHeader.appendChild(sectionIcon);

      const sectionTitle = createElement('span', { className: 'sidebar-section-title' }, section.title);
      sectionHeader.appendChild(sectionTitle);

      const chevron = createElement('span', { className: 'sidebar-section-chevron' }, '▼');
      sectionHeader.appendChild(chevron);

      // Toggle section on click
      sectionHeader.addEventListener('click', () => {
        state.sectionStates[section.id] = !state.sectionStates[section.id];
        localStorage.setItem('sidebarSections', JSON.stringify(state.sectionStates));
        sectionEl.classList.toggle('collapsed', state.sectionStates[section.id]);
      });

      sectionHeader.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          sectionHeader.click();
        }
      });

      sectionEl.appendChild(sectionHeader);

      // Section items
      const itemsContainer = createElement('div', { className: 'sidebar-section-items' });

      section.items.forEach(item => {
        const isActive = currentPath === item.path;
        const navItem = createElement('a', {
          className: `nav-item ${isActive ? 'active' : ''}`,
          href: escapeHtml(item.path),
          title: state.isCollapsed ? item.label : '',
          dataset: { path: item.path }
        });

        // Icon
        const iconEl = createElement('span', { className: 'nav-icon' }, item.icon);
        navItem.appendChild(iconEl);

        // Label
        if (!state.isCollapsed) {
          const labelEl = createElement('span', { className: 'nav-label' }, item.label);
          navItem.appendChild(labelEl);
        }

        // Tag (e.g., "DEV")
        if (item.tag && !state.isCollapsed) {
          const tagEl = createElement('span', { className: 'under-dev-tag' }, item.tag);
          navItem.appendChild(tagEl);
        }

        // Badge
        if (item.badgeKey && state.badgeCounts[item.badgeKey] > 0) {
          const badgeEl = createElement('span', {
            className: 'nav-badge',
            dataset: { badgeKey: item.badgeKey }
          }, String(state.badgeCounts[item.badgeKey]));
          navItem.appendChild(badgeEl);
        }

        // Click handler for SPA navigation
        navItem.addEventListener('click', (e) => {
          e.preventDefault();
          handleNavigation(item.path);
        });

        itemsContainer.appendChild(navItem);
      });

      sectionEl.appendChild(itemsContainer);
      nav.appendChild(sectionEl);
    });

    sidebar.appendChild(nav);
    return sidebar;
  }, [location.pathname, handleNavigation, mobileMenuOpen]);

  // Rebuild sidebar
  const rebuildSidebar = useCallback(() => {
    if (containerRef.current) {
      // Clear existing
      containerRef.current.innerHTML = '';
      // Build and append new
      const newSidebar = buildSidebar();
      sidebarRef.current = newSidebar;
      containerRef.current.appendChild(newSidebar);
    }
  }, [buildSidebar]);

  // Update active state without full rebuild
  const updateActiveState = useCallback(() => {
    if (sidebarRef.current) {
      const navItems = sidebarRef.current.querySelectorAll('.nav-item');
      navItems.forEach(item => {
        const path = item.dataset.path;
        const isActive = path === location.pathname;
        item.classList.toggle('active', isActive);
      });
    }
  }, [location.pathname]);

  // Update badge counts
  const updateBadges = useCallback(() => {
    if (sidebarRef.current) {
      const state = stateRef.current;
      Object.keys(state.badgeCounts).forEach(key => {
        const badges = sidebarRef.current.querySelectorAll(`[data-badge-key="${escapeHtml(key)}"]`);
        badges.forEach(badge => {
          const count = state.badgeCounts[key];
          if (count > 0) {
            safeSetText(badge, String(count));
            badge.style.display = '';
          } else {
            badge.style.display = 'none';
          }
        });
      });
    }
  }, []);

  // Initial build
  useEffect(() => {
    rebuildSidebar();
  }, [rebuildSidebar]);

  // Rebuild on mobile menu toggle
  useEffect(() => {
    if (sidebarRef.current) {
      sidebarRef.current.classList.toggle('mobile-open', mobileMenuOpen);
    }
  }, [mobileMenuOpen]);

  // Update active state on route change
  useEffect(() => {
    updateActiveState();
  }, [location.pathname, updateActiveState]);

  // Fetch pending event requests count
  useEffect(() => {
    const fetchPendingCount = async () => {
      try {
        const token = await getAuthToken();
        const restOperation = get({
          apiName: 'ChulasAdminAPI',
          path: '/v1/admin/event-requests?status=pending',
          options: {
            headers: {
              Authorization: token,
            },
          },
        });
        const response = await restOperation.response;
        const data = await response.body.json();
        stateRef.current.badgeCounts.pendingEvents = data.requests?.length || 0;
        updateBadges();
      } catch (err) {
        console.error('Error fetching pending requests count:', err);
      }
    };

    fetchPendingCount();
  }, [getAuthToken, updateBadges]);

  // Fetch pending availability requests count
  useEffect(() => {
    const fetchPendingAvailabilityCount = async () => {
      try {
        const token = await getAuthToken();
        const restOperation = get({
          apiName: 'ChulasEmployeeAPI',
          path: '/v1/admin/scheduling/availability-requests?status=pending',
          options: {
            headers: {
              Authorization: token,
            },
          },
        });
        const response = await restOperation.response;
        const data = await response.body.json();
        stateRef.current.badgeCounts.pendingAvailability = data.requests?.length || 0;
        updateBadges();
      } catch (err) {
        console.error('Error fetching pending availability requests count:', err);
      }
    };

    fetchPendingAvailabilityCount();
  }, [getAuthToken, updateBadges]);

  // Fetch pending job applications count
  useEffect(() => {
    const fetchPendingApplicationsCount = async () => {
      try {
        const token = await getAuthToken();
        const restOperation = get({
          apiName: 'ChulasJobsAPI',
          path: '/v1/admin/jobs/applications?status=PENDING',
          options: {
            headers: {
              Authorization: token,
            },
          },
        });
        const response = await restOperation.response;
        const data = await response.body.json();
        stateRef.current.badgeCounts.pendingApplications = data.applications?.length || 0;
        updateBadges();
      } catch (err) {
        console.error('Error fetching pending job applications count:', err);
      }
    };

    fetchPendingApplicationsCount();
  }, [getAuthToken, updateBadges]);

  // Render just a container - vanilla JS handles the rest
  return <div ref={containerRef} />;
};

export default Sidebar;
